import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { analytics } from '@/lib/api';

export default function AnalyticsTracker({ sections = [] }) {
  const location = useLocation();
  const { user } = useAuth();
  const tracked = useRef(false);
  const sectionTimers = useRef({});

  useEffect(() => {
    // Don't track admin users
    if (user?.is_admin) return;
    if (tracked.current) return;
    tracked.current = true;

    analytics.pageview({
      page: location.pathname,
      referrer: document.referrer || '',
      screen_size: `${window.innerWidth}x${window.innerHeight}`
    });

    return () => { tracked.current = false; };
  }, [location.pathname, user]);

  // Section tracking with Intersection Observer
  useEffect(() => {
    if (user?.is_admin || sections.length === 0) return;

    const observers = [];
    const timers = sectionTimers.current;

    sections.forEach(sectionId => {
      const el = document.getElementById(sectionId);
      if (!el) return;

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            timers[sectionId] = Date.now();
          } else if (timers[sectionId]) {
            const duration = Date.now() - timers[sectionId];
            if (duration > 500) {
              analytics.section({ page: location.pathname, section: sectionId, duration_ms: duration });
            }
            delete timers[sectionId];
          }
        });
      }, { threshold: 0.3 });

      observer.observe(el);
      observers.push(observer);
    });

    return () => {
      observers.forEach(o => o.disconnect());
      Object.entries(timers).forEach(([id, start]) => {
        const duration = Date.now() - start;
        if (duration > 500) {
          analytics.section({ page: location.pathname, section: id, duration_ms: duration });
        }
      });
      sectionTimers.current = {};
    };
  }, [location.pathname, sections, user]);

  return null;
}
